import { LeaveRequest, LeaveStatus } from '../types/leave';
import { LeaveService } from './leave';
import { LeaveAuditService } from './leaveAuditService';
import { sameIdentity } from './identity';
import { announceLeaveDecision } from './leaveNotifications';
import { loadAllUsers } from './userLoader';

export class LeaveApprovalService {
  static async checkLeaveOverlap(
    employeeEmail: string,
    startDate: string,
    endDate: string,
    excludeRequestId?: string
  ): Promise<{
    hasOverlap: boolean;
    overlappingRequest?: LeaveRequest;
    /** Which kind of clash it is, so callers can word the warning correctly. */
    overlapKind?: 'APPROVED' | 'PENDING';
  }> {
    const leaves = await LeaveService.listLeaves();

    // Pending requests count as blocking. Checking only APPROVED let an employee submit two
    // overlapping requests with no warning — neither saw the other, and the clash only
    // surfaced once one was approved and the second was already in the manager's queue.
    // The balance already subtracts pending days, so excluding them here had the two halves
    // of the system disagreeing about what a pending request means.
    const blocking = leaves.filter(l => {
      if (!sameIdentity(l.employeeEmail, employeeEmail)) return false;
      if (l.id === excludeRequestId) return false;
      const status = String(l.status).toUpperCase();
      return status === 'APPROVED' || status === 'SUBMITTED' || status === 'PENDING';
    });

    const reqStart = new Date(startDate).getTime();
    const reqEnd = new Date(endDate).getTime();

    // Approved clashes are reported ahead of pending ones: a confirmed absence is the more
    // definitive problem, and the caller shows a different message for each.
    const ordered = [
      ...blocking.filter(l => String(l.status).toUpperCase() === 'APPROVED'),
      ...blocking.filter(l => String(l.status).toUpperCase() !== 'APPROVED'),
    ];

    for (const l of ordered) {
      const lStart = new Date(l.startDate).getTime();
      const lEnd = new Date(l.endDate).getTime();
      if (reqStart <= lEnd && reqEnd >= lStart) {
        return {
          hasOverlap: true,
          overlappingRequest: l,
          overlapKind: (String(l.status).toUpperCase() === 'APPROVED' ? 'APPROVED' : 'PENDING') as 'APPROVED' | 'PENDING',
        };
      }
    }

    return { hasOverlap: false };
  }

  static async updateLeaveStatus(
    leaveId: string,
    newStatus: LeaveStatus,
    updatedBy: string,
    reason?: string
  ): Promise<LeaveRequest | null> {
    try {
      // Resolve the previous status from the full list rather than localStorage alone —
      // a leave created on another machine exists on the server but not in this browser,
      // and the approver is almost never the person who applied.
      const all = await LeaveService.listLeaves();
      const prev = all.find(l => l.id === leaveId);
      if (!prev) {
        console.warn(`[LeaveApprovalService] leave ${leaveId} not found; nothing to update`);
        return null;
      }

      // Server-backed. This used to write only to localStorage, which meant an approval
      // never left the approver's browser — the applicant's calendar never saw it.
      const updated = await LeaveService.updateLeave({
        id: leaveId,
        status: newStatus,
        updatedBy,
        decisionReason: reason,
      });

      if (!updated) return null;

      LeaveAuditService.logAction(
        leaveId,
        `Leave Status Changed to ${newStatus}`,
        updatedBy,
        prev.status,
        newStatus,
        reason || `Status updated to ${newStatus}`,
        prev.employeeEmail,
        prev.employeeName
      );

      // Force instant live updates across Shift Roster, Leave Balance Cards, and Chatbot
      window.dispatchEvent(new Event('leave-requests-updated'));
      window.dispatchEvent(new Event('leave-balance-updated'));
      window.dispatchEvent(new Event('storage'));

      /*
       * Announced here rather than by the approvals page, for the reason the certification
       * chain is announced by its service: the call site is exactly where it gets forgotten.
       * Behind this method, a second screen that ever approves a leave cannot reintroduce the
       * silence, and the ordering rule — the status is already persisted above — holds for
       * every caller rather than for the one that remembered it.
       *
       * Awaited, and safe to await: announceLeaveDecision catches everything, so a bounced
       * email cannot turn a recorded approval into a `null` return that the page reads as
       * "could not update the request".
       */
      await this.announce(updated, newStatus, updatedBy, reason);

      return updated;
    } catch (error) {
      console.error('[LeaveApprovalService] updateLeaveStatus error:', error);
      return null;
    }
  }

  /**
   * Resolve who to copy, then announce the decision.
   *
   * The leave row carries no managerEmail — the GraphQL type has no such field — so the
   * reporting line is read from the directory. `loadAllUsers` rather than a targeted lookup
   * because it is the cached call every other leave screen already makes, so this costs nothing
   * on a page that has loaded once.
   *
   * Private and best-effort: a directory that cannot be reached must still not cost the
   * approver their decision, so the announcement goes out with whatever was resolvable.
   */
  private static async announce(
    leave: LeaveRequest,
    status: LeaveStatus,
    decidedBy: string,
    reason?: string
  ): Promise<void> {
    let managerEmail: string | undefined;
    let managerName: string | undefined;
    try {
      const users = await loadAllUsers();
      const applicant = users.find(u => sameIdentity(u.email, leave.employeeEmail));
      managerEmail = applicant?.managerEmail;
      const manager = managerEmail
        ? users.find(u => sameIdentity(u.email, managerEmail as string))
        : undefined;
      managerName = manager?.fullName || manager?.name;
    } catch (error) {
      console.warn('[LeaveApprovalService] could not resolve the reporting manager:', error);
    }

    // The decider, when they are the manager and the directory knew their name — otherwise the
    // identifier that was recorded, which is what the audit trail carries too.
    const decidedByName =
      (managerEmail && sameIdentity(managerEmail, decidedBy) && managerName) || decidedBy;

    await announceLeaveDecision(leave, status, decidedByName, {
      managerEmail: managerEmail || decidedBy,
      managerName,
      decisionReason: reason,
    });
  }
}
